/**
 * Member profile route (Organizers only)
 * View and manage member details
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useUser, useUpdateUser } from '@/hooks/useUsers';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useToast } from '@/components/ui/ToastProvider';
import { DelegationSection } from '@/components/users/DelegationSection';
import { MemberPreferences } from '@/components/preferences/MemberPreferences';
import { useAuth } from '@/hooks/useAuth';

export const Route = createFileRoute('/users/$id')({
  beforeLoad: requireOrganizer,
  component: UserDetailComponent,
});

// Generate a consistent color from a string (user name)
function getAvatarColor(name: string): string {
  const colors = [
    '#5B4FE9', // Purple
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#3B82F6', // Blue
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#14B8A6', // Teal
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

// Get initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function UserDetailComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const userId = parseInt(id, 10);
  const { data: user, isLoading, error, refetch: refetchUser } = useUser(userId);
  const updateMutation = useUpdateUser();
  const { user: currentUser } = useAuth();

  const [isEditing, setIsEditing] = useState(false);

  const form = useForm({
    defaultValues: {
      isOrganizer: user?.isOrganizer ?? false,
      defaultAvailability: user?.defaultAvailability ?? false,
      goal: user?.goal ?? 'maintenance',
    },
    onSubmit: async ({ value }) => {
      try {
        await updateMutation.mutateAsync({
          id: userId,
          data: {
            isOrganizer: value.isOrganizer,
            defaultAvailability: value.defaultAvailability,
            goal: value.goal,
          },
        });
        toast.success(t('users.updateSuccess'));
        setIsEditing(false);
        refetchUser();
      } catch {
        toast.error(t('users.updateError'));
      }
    },
  });

  // Update form when user data changes
  if (user && (form.state.values.isOrganizer !== user.isOrganizer || form.state.values.defaultAvailability !== user.defaultAvailability || form.state.values.goal !== user.goal)) {
    form.setFieldValue('isOrganizer', user.isOrganizer);
    form.setFieldValue('defaultAvailability', user.defaultAvailability);
    form.setFieldValue('goal', user.goal ?? 'maintenance');
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate({ to: '/users' })}
            className="mb-4 text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            ← {t('users.backToMembers')}
          </button>
          <ErrorMessage
            title={t('users.notFound')}
            message={t('users.userNotFoundDescription')}
          />
        </div>
      </div>
    );
  }

  const avatarColor = getAvatarColor(user.name);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <button
          onClick={() => navigate({ to: '/users' })}
          className="mb-6 text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          ← {t('users.backToMembers')}
        </button>

        {/* Profile Header */}
        <div className="flex items-center gap-4 mb-8">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: avatarColor }}
          >
            {getInitials(user.name)}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">{user.slug}</span>
              <span className="text-gray-300">|</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  user.isOrganizer
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {user.isOrganizer ? t('users.organizer') : t('users.member')}
              </span>
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {t('users.userDetails')}
          </h2>

          <div className="space-y-4">
            {/* Role */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">{t('users.role')}</span>
              {isEditing ? (
                <form.Field
                  name="isOrganizer"
                  children={(field) => (
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        className="rounded border-gray-300 text-purple-600 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {t('users.makeOrganizer')}
                      </span>
                    </label>
                  )}
                />
              ) : (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isOrganizer
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {user.isOrganizer ? t('users.organizer') : t('users.member')}
                </span>
              )}
            </div>

            {/* Default Availability */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <span className="text-sm text-gray-600">{t('users.defaultAvailability')}</span>
              {isEditing ? (
                <form.Field
                  name="defaultAvailability"
                  children={(field) => (
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {t('users.enableDefaultAvailability')}
                      </span>
                    </label>
                  )}
                />
              ) : (
                <span className={`text-sm ${user.defaultAvailability ? 'text-green-600' : 'text-gray-500'}`}>
                  {user.defaultAvailability ? t('common.yes') : t('common.no')}
                </span>
              )}
            </div>

            {/* Goal */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <span className="text-sm text-gray-600">{t('users.goal')}</span>
              {isEditing ? (
                <form.Field
                  name="goal"
                  children={(field) => (
                    <select
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(
                          e.target.value as 'weight_loss' | 'maintenance' | 'muscle_gain',
                        )
                      }
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    >
                      <option value="weight_loss">{t('goal.weight_loss')}</option>
                      <option value="maintenance">{t('goal.maintenance')}</option>
                      <option value="muscle_gain">{t('goal.muscle_gain')}</option>
                    </select>
                  )}
                />
              ) : (
                <span className="text-sm text-gray-900">
                  {t(`goal.${user.goal ?? 'maintenance'}`)}
                </span>
              )}
            </div>

            {/* Created At */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <span className="text-sm text-gray-600">{t('users.createdAt')}</span>
              <span className="text-sm text-gray-900">
                {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>

            {/* Edit Button */}
            <div className="pt-4 border-t border-gray-100">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  {t('users.edit')}
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => form.handleSubmit()}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? t('app.saving') : t('app.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      form.reset();
                      setIsEditing(false);
                    }}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('app.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="mt-6">
          <MemberPreferences userId={userId} />
        </div>

        {/* Delegation section - only for organizers */}
        {currentUser?.isOrganizer && (
          <div className="mt-6">
            <DelegationSection user={user} />
          </div>
        )}
      </div>
    </div>
  );
}
